import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  CircularProgress, 
  Alert, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';

// Admin emails are now loaded dynamically from Firestore
import { useAdminEmails } from '../../hooks/useAdminEmails';

// Server URL from environment variable or fallback to localhost
const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const AdminUniswapPage: React.FC = () => {
  const { state } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawData, setRawData] = useState<any>(null);
  const navigate = useNavigate();

  // Check if the current user is an admin
  const { adminEmails, loading: adminLoading } = useAdminEmails();
const isAdmin = state.user && adminEmails.includes(state.user.email);

  useEffect(() => {
    // Redirect non-admin users
    if (state.user && !isAdmin) {
      navigate('/dashboard');
    }
  }, [state.user, isAdmin, navigate]);

  const fetchRawData = async () => {
    if (!state.user || !state.user.id) {
      setError('User not logged in');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching completely unmodified raw Uniswap data directly from server...');
      
      // Make a direct call to the server endpoint for raw data
      const response = await axios.get(`${serverUrl}/api/uniswap/raw-data/${state.user.id}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      setRawData(response.data);
    } catch (err: any) {
      console.error('Error fetching Uniswap raw data:', err);
      setError(err.response?.data?.message || 'Failed to fetch Uniswap data');
    } finally {
      setLoading(false);
    }
  };

  // If user is not authenticated or loading, show loading
  if (state.loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If user is not an admin, don't render anything (will be redirected)
  if (!isAdmin) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Uniswap Admin Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          This page displays the raw data returned from the Uniswap API, showing exactly what's coming from the API without any transformations.
        </Typography>
        
        <Box sx={{ mt: 3, mb: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={fetchRawData}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Fetch Raw Uniswap Data'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {rawData && (
          <Box sx={{ mt: 3 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Raw Uniswap API Response</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  backgroundColor: '#f5f5f5', 
                  p: 2, 
                  borderRadius: 1,
                  maxHeight: '500px',
                  overflow: 'auto'
                }}>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(rawData, null, 2)}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Display positions if available */}
            {rawData.positions && rawData.positions.length > 0 && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Liquidity Positions Table</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Pool ID</TableCell>
                          <TableCell>Token 0</TableCell>
                          <TableCell>Token 1</TableCell>
                          <TableCell>Fee Tier</TableCell>
                          <TableCell>Liquidity</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rawData.positions.map((position: any, index: number) => (
                          <TableRow key={index}>
                            <TableCell>{position.id || 'N/A'}</TableCell>
                            <TableCell>{position.token0?.symbol || 'N/A'}</TableCell>
                            <TableCell>{position.token1?.symbol || 'N/A'}</TableCell>
                            <TableCell>{position.pool?.feeTier || 'N/A'}</TableCell>
                            <TableCell>{position.liquidity || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default AdminUniswapPage;
